import { assetServices, companyServices, contractServices } from '@/services'
import { AttachmentUploader } from '@/shared/components/attachment-uploader'
import { Pill } from '@/shared/components/pill'
import { useUpdateAttachmentMutation } from '@/store/services/attachment.service'
import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader
} from '@material-tailwind/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath, useLoaderData, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

export const PendingAttachmentUpload = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentResource, setCurrentResource] = useState({})
  const { attachment: currentAttachment = {} } = useLoaderData()
  const currentUrlParams = useLocation().search

  const [updateAttachment, { isLoading, isSuccess }] =
    useUpdateAttachmentMutation()

  const onUpload = async (values) => {
    if (!values.document.filename) {
      toast.warn('Adjunta un documento antes de actualizar.')
      return
    }

    updateAttachment(values)
  }

  const attachmentTypeOptions = useMemo(() => {
    return [currentAttachment.attachmentType]
  }, [currentAttachment])

  const initialValues = useMemo(() => {
    return {
      id: currentAttachment.id,
      attachment_type_id: currentAttachment.attachmentType.id,
      document: {},
      expiration_date: currentAttachment.expirationDate,
      period_month: currentAttachment.periodMonth,
      period_year: currentAttachment.periodYear,
      status: 'pending'
    }
  }, [currentAttachment])

  const resourcePromise = () => {
    if (currentAttachment.attachableType === 'Contractor::Contract') {
      return contractServices.show({ id: currentAttachment.attachableId })
    }

    if (currentAttachment.attachableType === 'Contractor::Asset') {
      return assetServices.show({ id: currentAttachment.attachableId })
    }

    if (currentAttachment.attachableType === 'Contractor::Company') {
      return companyServices.show({ id: currentAttachment.attachableId })
    }
  }

  useEffect(() => {
    if (!isSuccess) return

    navigate(`/attachments${currentUrlParams}`)
  }, [isSuccess])

  useEffect(() => {
    if (!currentAttachment.id) return

    const fetchResource = async () => {
      const { data } = await resourcePromise()
      const [resource = {}] = Object.values(data)
      setCurrentResource(resource)
    }

    fetchResource()
  }, [currentAttachment])

  const pillOptions = useMemo(() => {
    if (!currentResource.id) return []

    if (currentResource.klassName === 'Contractor::Contract') {
      return [
        { label: t('company.name'), value: currentResource.company.name },
        {
          label: t('documentNumber.one'),
          value: currentResource.company.documentNumber
        },
        { label: t('contract.id'), value: currentResource.id }
      ]
    }

    if (currentAttachment.attachableType === 'Contractor::Asset') {
      return [
        {
          label: t('asset.vehicle.type'),
          value: currentResource.vehicleInfo.type
        },
        {
          label: t('asset.vehicle.patent'),
          value: currentResource.vehicleInfo.patent
        },
        { label: t('contract.id'), value: currentResource.contract.id },
        {
          label: t('documentNumber.one'),
          value: currentResource.contract.company.documentNumber
        }
      ]
    }

    if (currentAttachment.attachableType === 'Contractor::Company') {
      return [
        { label: t('company.name'), value: currentResource.name },
        {
          label: t('documentNumber.one'),
          value: currentResource.documentNumber
        }
      ]
    }

    return []
  }, [currentResource])

  return (
    <Dialog open size='lg'>
      <DialogHeader>Carga de documentos</DialogHeader>

      <DialogBody className='flex gap-4'>
        {pillOptions.map((pillOption) => (
          <Pill key={pillOption.label} {...pillOption} />
        ))}
      </DialogBody>

      <DialogBody>
        {attachmentTypeOptions.length && Object.keys(initialValues).length && (
          <AttachmentUploader
            isDisabled
            attachableType={currentAttachment.attachableType}
            attachmentTypes={attachmentTypeOptions}
            initialValues={initialValues}
            showDefaultHeader={false}
            onUpload={onUpload}
          >
            {(handleSubmit) => (
              <div className='flex justify-between'>
                <Link to={generatePath(`/attachments${currentUrlParams}`)}>
                  <Button
                    variant='text'
                  >
                    Cancelar
                  </Button>
                </Link>
                <Button loading={isLoading} onClick={handleSubmit}>
                  Cargar archivo
                </Button>
              </div>
            )}
          </AttachmentUploader>
        )}
      </DialogBody>
    </Dialog >
  )
}
